import type { QueryKey } from "@tanstack/react-query";

import { ErrorState, LoadingState, OfflineState } from "@/ui/states";

import { isServerAnsweredFailure } from "./queryClient";
import { useRetryPausedQuery } from "./useRetryPausedQuery";

/**
 * Renders the right surface state for a query that has not resolved.
 *
 * `isPending` is not one condition but two, and issue #215 is what happens when
 * a surface treats them as one. A query that is *fetching* deserves a skeleton:
 * something is in flight and will arrive. A query that is *paused* has nothing
 * in flight — TanStack Query is holding the retry — and a skeleton there is a
 * lie that never resolves.
 *
 * It is three conditions, in fact, and #327 is what happens when the second and
 * third are treated as one. A retry is held for two independent reasons: the
 * browser reports no connection, or the document is hidden — see the focus
 * clause documented on `NETWORK_MODE` in `queryClient.ts`. Only the first is
 * "offline". A 503 the Editor Service really sent, parked because the tab was
 * in the background, was being reported to the clinician as a connectivity
 * problem *and* promised to recover on its own. `isServerAnsweredFailure` tells
 * the two apart from the one fact that distinguishes them: whether a response
 * ever arrived.
 *
 * Surfaces route their pending branch through this rather than branching
 * themselves, so the distinction is made once instead of five times — and so
 * is the retry. It is built here from the query key rather than accepted as a
 * callback because the obvious callback, `query.refetch()`, silently does
 * nothing on exactly the query this surface describes; see
 * `useRetryPausedQuery` for the mechanism. That applies to the answered-failure
 * branch too: it is still a *paused* query, so its "Try again" needs the same
 * cancel-then-refetch, not the `refetch` the surface's own error branch uses.
 */
export function QueryPendingState({
  isPaused,
  failureReason,
  label,
  errorTitle,
  rows,
  queryKey,
  className,
}: {
  /** `query.isPaused` — no request in flight, the retry is being held. */
  isPaused: boolean;
  /**
   * `query.failureReason` — the failure the held retry would be retrying.
   *
   * Required, deliberately. The old behaviour is what a call site gets by
   * omitting it, and #327's own risk assessment named "missing one leaves that
   * surface with the old behaviour and no failing test" as the main hazard of
   * this change. A required prop is what makes the compiler, rather than a
   * review checklist, the thing that closes it.
   */
  failureReason: unknown;
  /** Accessible label for the loading case, e.g. "Loading projects". */
  label: string;
  /**
   * Heading for a pause the Editor Service answered.
   *
   * This is the same failure the surface's own error branch renders once the
   * retries are spent, so pass that branch's title verbatim: the clinician sees
   * one screen turn into the other, and two headings for one failure would read
   * as two different problems.
   */
  errorTitle: string;
  rows?: number;
  /** The pending query's key, e.g. `queryKeys.projects(search)`. */
  queryKey: QueryKey;
  className?: string;
}) {
  const retry = useRetryPausedQuery(queryKey);

  if (isPaused) {
    if (isServerAnsweredFailure(failureReason)) {
      // `ErrorState` rather than `QueryErrorState`: every branch that component
      // adds — expired sign-in, denial, "not implemented" — is for a TERMINAL
      // status, and a terminal status settles as an error instead of pausing
      // (see `isServerAnsweredFailure`). Routing through it would add a
      // `subject` prop to six call sites to feed a heading none of them can
      // reach.
      return (
        <ErrorState
          title={errorTitle}
          onRetry={retry}
          {...(className !== undefined ? { className } : {})}
        />
      );
    }

    return (
      <OfflineState
        onRetry={retry}
        {...(className !== undefined ? { className } : {})}
      />
    );
  }

  return (
    <LoadingState
      label={label}
      {...(rows !== undefined ? { rows } : {})}
      {...(className !== undefined ? { className } : {})}
    />
  );
}
